import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'
import { getAllUsers } from '../../api/user/list-all-users'
import { formatListAllUsersV1, formatListAllUsersV2 } from './user-management-helpers'
import { listUsers } from '../../api/userauthentication/user-management'
import { getAllRoles } from '../../api/user/list-all-roles'
import { appNameConstants } from '../../constants/app-constants'
import { userTypeConstants } from '../../constants/user-constants'

/* Initial state of the slice */
const initialState = {
  isFetchingAllUsers: false,
  users: [],
  fetchAllUsersError: null,
  isFetchingAllRoles: false,
  roles: [],
  fetchAllRolesError: null,
}

/* Handler for making service call to fetch all users */
export const fetchAllUsers = createAsyncThunk(
  'userManagement/fetchAllUsers',
  async (args, { rejectWithValue }) => {
    try {
      let response
      let formattedUsers
      if (!args?.isUserServiceEnabled) {
        response = await listUsers()
        formattedUsers = formatListAllUsersV1(response)
      }
      if (args?.isUserServiceEnabled) {
        response = await getAllUsers()
        formattedUsers = formatListAllUsersV2(response.data.data)
      }
      const usersSortedByDateAdded = formattedUsers.sort((a, b) =>
        b.dateAdded.localeCompare(a.dateAdded),
      )
      return usersSortedByDateAdded
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

/* Handler for making service call to fetch all roles */
export const fetchAllRoles = createAsyncThunk(
  'userManagement/fetchAllRoles',
  async (args, { rejectWithValue }) => {
    try {
      if (!args?.isUserServiceEnabled) {
        return {
          appName: appNameConstants.CLIENT_PORTAL,
          roles: [
            { roleName: userTypeConstants.CLIENT, roleDescription: userTypeConstants.CLIENT },
          ],
        }
      }
      const response = await getAllRoles()
      return response.data?.data?.roles
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    resetFetchAllUsersError(state) {
      state.fetchAllUsersError = null
    },
    resetFetchAllRolesError(state) {
      state.fetchAllRolesError = null
    },
  },
  extraReducers: (builder) => {
    builder
      /* Fetching Users */
      .addCase(fetchAllUsers.pending, (state) => {
        state.isFetchingAllUsers = true
        state.users = []
        state.fetchAllUsersError = null
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.isFetchingAllUsers = false
        state.users = action.payload
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.isFetchingAllUsers = false
        state.fetchAllUsersError = action.payload
      })
      /* Fetching Roles */
      .addCase(fetchAllRoles.pending, (state) => {
        state.isFetchingAllRoles = true
        state.roles = null
        state.fetchAllRolesError = null
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isFetchingAllRoles = false
        state.roles = action.payload
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isFetchingAllRoles = false
        state.fetchAllRolesError = action.payload
      })
      /* Action which gets triggered
         when running persistor.purge() */
      .addCase(PURGE, () => {
        return initialState
      })
  },
})

export const { resetFetchAllUsersError, resetFetchAllRolesError } = userManagementSlice.actions

export default userManagementSlice.reducer
